// src/app/core/shacl-api.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { map, retryWhen, mergeMap, catchError } from 'rxjs/operators';
import { NodeShapeDTO } from '../models/ShaclShapeSummary';
import { API_BASE_SDSERVICE } from 'src/environments/api';


@Injectable({ providedIn: 'root' })
export class ShaclApiService {
  private readonly base = API_BASE_SDSERVICE;
  private readonly maxRetries = 3;
  private readonly initialDelay = 1000; // 1 second
  private readonly maxDelay = 10000; // 10 seconds
  private readonly forbiddenPaths = new Set<string>([
    'https://w3id.org/gaia-x/development#purpose',
    'https://w3id.org/gaia-x/development#legalbasis',
    'https://w3id.org/gaia-x/development#consentwithdrawalcontactpoint',
    'https://w3id.org/gaia-x/development#dataprotectioncontactpoint',
  ]);
  private shapeCache = new Map<string, Observable<NodeShapeDTO>>();

  constructor(private http: HttpClient) { }

  listShapes(query?: string, source?: 'W3ID_DEV' | 'NPM_UNPKG'): Observable<NodeShapeDTO[]> {
    const params: string[] = [];
    if (query) params.push(`q=${encodeURIComponent(query)}`);
    if (source) params.push(`source=${encodeURIComponent(source)}`);
    const url = `${this.base}/shapes/shapes${params.length ? `?${params.join('&')}` : ''}`;

    return this.http.get<NodeShapeDTO[] | any>(url).pipe(
      map(response => {
        // Backend should return an array; handle 502 shaped payloads defensively
        if (response && (response as any).status === 502) {
          throw { status: 502 } as HttpErrorResponse;
        }
        if (!Array.isArray(response)) {
          throw new Error('Invalid shape response: expected an array');
        }

        return response as NodeShapeDTO[];
      }),
      retryWhen(errors =>
        errors.pipe(
          mergeMap((error: HttpErrorResponse | any, attemptIndex) => {
            const retryAttempt = attemptIndex + 1;
            const status = error?.status ?? (error as any)?.status;
            const isServerError = status === 502 || (status && status >= 500);
            if (!isServerError || retryAttempt > this.maxRetries) {
              return throwError(() => error);
            }
            const delay = Math.min(
              this.initialDelay * Math.pow(2, attemptIndex),
              this.maxDelay
            );
            return timer(delay);
          })
        )
      ),
      catchError(error => throwError(() => error))
    );
  }

  // GET /shapes/:iri → full NodeShape details (encoded IRI)
  getShape(query: string, source?: 'W3ID_DEV' | 'NPM_UNPKG'): Observable<NodeShapeDTO> {
    const cacheKey = `${query}_${source || 'default'}`;
    if (this.shapeCache.has(cacheKey)) {
      return this.shapeCache.get(cacheKey)!;
    }

    const q = encodeURIComponent(query);
    const url = `${this.base}/rules/rules?q=${q}${source ? `&source=${encodeURIComponent(source)}` : ''}`;

    const obs = this.http.get<NodeShapeDTO | any>(url).pipe(
      map(response => {
        if (response && (response as any).status === 502) {
          throw { status: 502 } as HttpErrorResponse;
        }
        if (!this.isValidNodeShape(response)) {
          throw new Error('Invalid shape response: missing required fields');
        }
        if (this.containsForbiddenFields(response as NodeShapeDTO)) {
          throw new Error(`Forbidden SHACL fields detected for query: ${query}`);
        }
        return response as NodeShapeDTO;
      }),
      retryWhen(errors =>
        errors.pipe(
          mergeMap((error: HttpErrorResponse | any, attemptIndex) => {
            const retryAttempt = attemptIndex + 1;
            const status = error?.status ?? (error as any)?.status;
            const isServerError = status === 502 || (status && status >= 500);
            if (!isServerError || retryAttempt > this.maxRetries) {
              return throwError(() => error);
            }
            const delay = Math.min(
              this.initialDelay * Math.pow(2, attemptIndex),
              this.maxDelay
            );
            return timer(delay);
          })
        )
      ),
      catchError(error => {
        this.shapeCache.delete(cacheKey); // Don't cache errors
        return throwError(() => error);
      }),
      // Share replay so multiple subscribers don't trigger multiple requests
      map(res => res as NodeShapeDTO)
    );

    this.shapeCache.set(cacheKey, obs);
    return obs;
  }

  private isValidNodeShapeList(list: any[]): list is NodeShapeDTO[] {
    return Array.isArray(list) && list.every(item => this.isValidNodeShape(item));
  }

  private isValidNodeShape(shape: any): shape is NodeShapeDTO {
    return !!(
      shape &&
      typeof shape === 'object' &&
      typeof shape.id === 'string' &&
      typeof shape.targetClass === 'string' &&
      typeof shape.closed === 'boolean' &&
      Array.isArray(shape.ignoredProperties) &&
      Array.isArray(shape.properties)
    );
  }


  private containsForbiddenFields(shape: NodeShapeDTO): boolean {
    return Array.isArray(shape.properties) && shape.properties.some(f => {
      const p = (f?.path ?? '').toLowerCase();
      return this.forbiddenPaths.has(p);
    });
  }

  getSavedSdsByUserId(userId: string): Observable<any[]> {
    const url = `${this.base}/api/sd/saved/user/${userId}`;
    return this.http.get<any[]>(url).pipe(
      catchError(error => throwError(() => error))
    );
  }

  getSavedSdsByOrgId(orgId: string): Observable<any[]> {
    const url = `${this.base}/api/sd/saved/org/${orgId}`;
    return this.http.get<any[]>(url).pipe(
      catchError(error => throwError(() => error))
    );
  }

  validate(sd: any): Observable<any> {
    const url = `${this.base}/sd/validate`;
    return this.http.post(url, sd);
  }

  private containsForbiddenFieldsInList(list: NodeShapeDTO[]): boolean {
    return list.some(s => this.containsForbiddenFields(s));
  }
}
